import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { RoadmapResponse } from "../../api/client";
import { Redact } from "./Redact";

/**
 * RoadmapMock — a screenshot-style illustration of a user's personalized roadmap.
 *
 * Renders inside a faux browser chrome to signal "this is what the product
 * shows you." Used in two modes:
 *
 *   - No `roadmap` prop → shows an anonymized example with staged phase
 *     states (COMPLETE / IN PROGRESS / UPCOMING) so the reader sees what a
 *     populated roadmap looks like in different states. Header reads
 *     "EXAMPLE ROADMAP."
 *
 *   - `roadmap` prop provided → shows the authenticated user's real roadmap.
 *     All phases render as UPCOMING with 0/N tasks because task-level
 *     completion tracking isn't implemented yet; the "GENERATED X AGO"
 *     framing keeps this honest for a freshly-generated roadmap.
 */

// Inline monospace stack — a font, not a colour token.
const MONO = `ui-monospace, 'SF Mono', Menlo, Consolas, monospace`;

interface PhaseView {
  weeksLabel: string;
  title: string;
  done: number;
  total: number;
  state: "COMPLETE" | "IN PROGRESS" | "UPCOMING";
}

const MOCK_PHASE_DEFS: {
  weeksLow: number;
  weeksHigh: number;
  titleKey: string;
  done: number;
  total: number;
  state: PhaseView["state"];
}[] = [
  { weeksLow: 1, weeksHigh: 2, titleKey: "RoadmapMock.phaseAuditOrient", done: 3, total: 3, state: "COMPLETE" },
  { weeksLow: 3, weeksHigh: 5, titleKey: "RoadmapMock.phaseCloseAgenticGap", done: 2, total: 5, state: "IN PROGRESS" },
  { weeksLow: 6, weeksHigh: 8, titleKey: "RoadmapMock.phaseShipEvaluatedProject", done: 0, total: 4, state: "UPCOMING" },
  { weeksLow: 9, weeksHigh: 10, titleKey: "RoadmapMock.phaseNarrativePortfolio", done: 0, total: 4, state: "UPCOMING" },
  { weeksLow: 11, weeksHigh: 12, titleKey: "RoadmapMock.phasePipelineInterviews", done: 0, total: 5, state: "UPCOMING" },
];

const STATE_LABEL_KEYS: Record<PhaseView["state"], string> = {
  COMPLETE: "RoadmapMock.stateComplete",
  "IN PROGRESS": "RoadmapMock.stateInProgress",
  UPCOMING: "RoadmapMock.stateUpcoming",
};

const AUDIENCE_SHORT_KEYS: Record<string, string> = {
  swe_to_ai: "RoadmapMock.audienceSweToAi",
  pm_to_ai_pm: "RoadmapMock.audiencePmToAiPm",
  da_to_ai: "RoadmapMock.audienceDsToAi",
};

function formatRelative(t: TFunction, iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.max(0, Math.round((now - then) / 60000));
  if (mins < 1) return t("RoadmapMock.justNow");
  if (mins < 60) return t("RoadmapMock.minutesAgo", { count: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t("RoadmapMock.hoursAgo", { count: hours });
  const days = Math.round(hours / 24);
  if (days < 30) return t("RoadmapMock.daysAgo", { count: days });
  const months = Math.round(days / 30);
  return t("RoadmapMock.monthsAgo", { count: months });
}

interface RoadmapMockProps {
  roadmap?: RoadmapResponse;
}

export function RoadmapMock({ roadmap }: RoadmapMockProps) {
  const theme = useTheme();
  const { t } = useTranslation("career-transition");
  const isReal = !!roadmap;

  // Derived palette values — computed once per render from the live theme.
  const coral = theme.palette.primary.main;
  const coralDeep = theme.palette.primary.dark;
  const coralWashStrong = alpha(coral, 0.12);
  const divider = theme.palette.divider;
  const bg = theme.palette.background.default;
  const paper = theme.palette.background.paper;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const textDisabled = theme.palette.text.disabled;
  const shadowXl = theme.shadows[8];

  const phases: PhaseView[] = useMemo(() => {
    if (!roadmap) {
      return MOCK_PHASE_DEFS.map((p) => ({
        weeksLabel: t("RoadmapMock.weeksRange", { low: p.weeksLow, high: p.weeksHigh }),
        title: t(p.titleKey),
        done: p.done,
        total: p.total,
        state: p.state,
      }));
    }
    return roadmap.phases.map((p) => {
      const totalTasks = (p.weeks ?? []).reduce(
        (sum, w) => sum + (w.tasks ?? []).length,
        0,
      );
      return {
        weeksLabel: t("RoadmapMock.weeksRange", { low: p.week_range[0], high: p.week_range[1] }),
        title: p.title,
        done: 0,
        total: totalTasks,
        state: "UPCOMING" as const,
      };
    });
  }, [roadmap, t]);

  const headerLabel = isReal
    ? t("RoadmapMock.personalizedHeader", { relative: formatRelative(t, roadmap.created_at) })
    : t("RoadmapMock.exampleHeader");

  const readinessScore = isReal ? roadmap.overall.score : 68;
  const archetypeLabelKey = isReal
    ? AUDIENCE_SHORT_KEYS[roadmap.target_audience]
    : undefined;
  const archetypeLabel = isReal
    ? archetypeLabelKey
      ? t(archetypeLabelKey)
      : roadmap.target_audience
    : t("RoadmapMock.audienceSweToAi");
  const urlSuffix = isReal ? roadmap.id.slice(0, 6) : "██████";

  return (
    <div
      style={{
        background: paper,
        border: `1px solid ${divider}`,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: shadowXl,
      }}
    >
      {/* faux browser chrome */}
      <div
        style={{
          height: 38,
          borderBottom: `1px solid ${divider}`,
          display: "flex",
          alignItems: "center",
          padding: "0 14px",
          gap: 8,
          background: bg,
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: bg,
                border: `1px solid ${divider}`,
              }}
            />
          ))}
        </div>
        <div
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: 11,
            color: textDisabled,
            fontFamily: MONO,
          }}
        >
          {t("RoadmapMock.addressBar", { urlSuffix })}
        </div>
      </div>

      {/* header band */}
      <div
        style={{
          padding: "20px 24px 16px",
          borderBottom: `1px solid ${divider}`,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: coral,
            letterSpacing: 0.8,
            marginBottom: 8,
          }}
        >
          {headerLabel}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 500,
              color: textPrimary,
              letterSpacing: -0.4,
            }}
          >
            {isReal ? (
              <span>{archetypeLabel}</span>
            ) : (
              <>
                <Redact width={120} />
                <span style={{ color: textSecondary, fontWeight: 500 }}>
                  {" "}
                  {t("RoadmapMock.toAiEngineerSuffix")}
                </span>
              </>
            )}
          </div>
          <div
            style={{
              display: "flex",
              gap: 16,
              fontSize: 12,
              color: textSecondary,
            }}
          >
            <span>
              {t("RoadmapMock.readinessLabel")}{" "}
              <span style={{ color: textPrimary, fontWeight: 500 }}>
                {t("RoadmapMock.readinessScore", { score: readinessScore })}
              </span>
            </span>
            {!isReal && (
              <span>
                {t("RoadmapMock.patternLabel")}{" "}
                <span style={{ color: textPrimary, fontWeight: 500 }}>
                  {archetypeLabel}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* phases */}
      <div>
        {phases.map((p, i) => {
          const isActive = p.state !== "UPCOMING";
          return (
            <div
              key={i}
              style={{
                padding: "14px 24px",
                borderTop: i === 0 ? "none" : `1px solid ${divider}`,
                display: "grid",
                gridTemplateColumns: "100px 1fr auto",
                gap: 20,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: textDisabled,
                  letterSpacing: 0.4,
                }}
              >
                {p.weeksLabel.toUpperCase()}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 14,
                    color: textPrimary,
                    fontWeight: 500,
                    marginBottom: 6,
                  }}
                >
                  {p.title}
                </div>
                <div
                  style={{ display: "flex", gap: 4, alignItems: "center" }}
                >
                  {Array.from({ length: p.total }).map((_, k) => (
                    <span
                      key={k}
                      style={{
                        width: 22,
                        height: 4,
                        borderRadius: 2,
                        background: k < p.done ? coral : divider,
                      }}
                    />
                  ))}
                  <span
                    style={{
                      fontSize: 11,
                      color: textSecondary,
                      marginLeft: 8,
                    }}
                  >
                    {t("RoadmapMock.tasksCount", { done: p.done, total: p.total })}
                  </span>
                </div>
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: isActive ? coralWashStrong : alpha(theme.palette.text.primary, 0.04),
                  color: isActive ? coralDeep : textSecondary,
                  letterSpacing: 0.3,
                }}
              >
                {t(STATE_LABEL_KEYS[p.state])}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RoadmapMock;
